import { InstrumentIcon, PaymentModuleId } from '@foundation/apis/payments';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { useEffect, useMemo, useState, JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  disableUIBlocker,
  enableUIBlocker,
  FilledBadge,
  LoadingSpinner,
  LoadingSpinnerSize,
  Paragraph,
  PaymentIcon,
  QuestionMarkIcon,
} from '@/components/common';
import { AlertWarning } from '@/components/common/alerts';
import { BadgeType } from '@/components/common/badges/enums';
import { Tooltip } from '@/components/common/others/Tooltip/Tooltip';
import { RedirectStatus } from '@/components/contexts/params';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { useUserFriendlyPrefixForInstrumentBrand } from '@/hooks/useUserFriendlyPrefixForInstrumentBrands';
import { triggerErrorToast } from '@/libs/toasts';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import BraintreeCardService from '@/services/braintree/BraintreeCardService';
import { BraintreeError } from '@/services/braintree/errors/BraintreeError';
import { isUnsupportedCard } from '@/services/braintree/helpers/BraintreeValidationHelper';
import { BTCCValidationResults } from '@/services/braintree/schemas/ValidationResults';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import { mapBrandNameToInstrumentBrand } from '@/services/payments/instruments/PaymentInstrumentsHelper';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { getQaTestingProps, QaTestingElements } from '@/utils/qaTesting';

interface IBraintreeCardFormProps {
  readonly navigation?: JSX.Element | null;
}

const styles = {
  hostedFieldContainer:
    'relative rounded-field bg-secondary h-14 p-sm focused-container ring-silver-700 ring-1 shadow-on-focus',
  hostedFieldContainerInError: 'border-2 border-error',
};

export function BraintreeCardForm({ navigation }: IBraintreeCardFormProps) {
  const { userId } = useAuth();
  const { t } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();
  const instrumentBrandPrefixer = useUserFriendlyPrefixForInstrumentBrand();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const [cardTypeBeingWritten, setCardTypeBeingWritten] =
    useState<InstrumentIcon>(InstrumentIcon.GenericCard);
  const [formValidation, setFormValidation] = useState<BTCCValidationResults>();
  const [searchParams, setSearchParams] = useSearchParams();

  const commonErrorMessage = useMemo(() => {
    return (
      <div className="absolute left-1 -top-3.5">
        <FilledBadge
          type={BadgeType.Error}
          text={t('Invalid')}
        />
      </div>
    );
  }, [t]);

  const submitCardForm = () => {
    enableUIBlocker();

    let brandPrefix = '';

    if (cardTypeBeingWritten !== undefined) {
      const brand = mapBrandNameToInstrumentBrand(cardTypeBeingWritten);
      if (brand !== undefined) {
        const prefixer = instrumentBrandPrefixer(brand);
        if (prefixer !== undefined) {
          brandPrefix = prefixer.toUpperCase();
        }
      }
    }

    let promise;
    if (PaymentStore.flow === PaymentIntentFlow.Moto) {
      promise = BraintreeCardService.authorizeWithoutVerification();
    } else if (PaymentStore.flow === PaymentIntentFlow.Manage) {
      promise = BraintreeCardService.verifyAndTokenizeNewInstrument(userId);
    } else {
      promise = BraintreeCardService.verifyAndAuthorizeNewInstrument();
    }
    promise
      .then(() => {
        if (PaymentStore.flow === PaymentIntentFlow.Manage) {
          searchParams.set('status', RedirectStatus.Success);
          setSearchParams(searchParams);
          window.location.reload();
        } else {
          NavigationService.navigateTo({ page: InternalPage.StatusSuccess });
          disableUIBlocker();
        }
        Clarity.setTag('pay', [
          RedirectStatus.Success,
          paymentModuleT(PaymentModuleId.BraintreeCard),
        ]);
      })
      .catch((err: Error | undefined) => {
        disableUIBlocker();
        TelemetryService.trackException(err, 'BraintreeCardForm');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'BraintreeCardForm failed to verify the card or authorize it.',
          err,
        );
        Clarity.setTag('pay', [
          RedirectStatus.Failure,
          paymentModuleT(PaymentModuleId.BraintreeCard),
        ]);

        if (err !== undefined && err.name === 'BraintreeError') {
          const braintreeError = err as BraintreeError;
          const isUnsupportedMethodByBraintreeMerchant =
            braintreeError !== undefined && isUnsupportedCard(braintreeError);

          if (isUnsupportedMethodByBraintreeMerchant) {
            if (brandPrefix === '') {
              triggerErrorToast(
                t('Unsupported payment method!'),
                t('The selected payment method can not be added.'),
              );
            } else {
              triggerErrorToast(
                t('Unsupported payment method!'),
                t('{{brandPrefix}} payment method can not be added.', {
                  brandPrefix: brandPrefix,
                }),
              );
            }
          }
        } else {
          triggerErrorToast(
            t('Something went wrong!'),
            t(
              'Operation failed. Please try again with a different payment method.',
            ),
          );
        }

        /**
         * Braintree 3DS card verification gets stuck when attempting to authorize again
         * with the same data. Reinitializing solves the issue.
         */
        BraintreeCardService.reInit3DS();
      });
  };

  useEffect(() => {
    setLoading(true);
    BraintreeCardService.setupHostedFields(
      {
        cardholderName: {
          container: '#cc-name',
          placeholder: t('Cardholder Name'),
        },
        number: {
          container: '#cc-number',
          placeholder: t('Card Number'),
        },
        cvv: { container: '#cc-cvv', placeholder: 'CVV' },
        expirationDate: {
          container: '#cc-expiration',
          placeholder: 'MM/YY',
        },
      },
      setCardTypeBeingWritten,
      setFormValidation,
    )
      .catch((err) => {
        TelemetryService.trackException(err, 'BraintreeCardForm');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'BraintreeCardForm failed to initialize form.',
          err,
        );
        setError(err);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative">
      {loading && !error && (
        <div className={'absolute w-full h-full flex justify-center'}>
          <LoadingSpinner size={LoadingSpinnerSize.Small} />
        </div>
      )}
      {error && (
        <AlertWarning
          title={t('Something went wrong!')}
          description={t('Card payments are currently unavailable.')}
        />
      )}
      {!error && (
        <>
          <div
            className={cn(
              'flex flex-col mt-md p-sm rounded-md border border-silver-700 bg-white',
              // We need the elements to be added to the DOM so that braintree SDK
              // can find them and initialize them. We hide them until all is ready.
              // In the meantime, a loading indicator is shown
              loading ? 'invisible' : 'visible',
            )}
          >
            <div
              className={cn(
                styles.hostedFieldContainer,
                formValidation?.invalidIds.includes('#cc-name') &&
                  styles.hostedFieldContainerInError,
              )}
              id="cc-name"
              {...getQaTestingProps(QaTestingElements.CardHolderName)}
            >
              {formValidation?.invalidIds.includes('#cc-name') &&
                commonErrorMessage}
            </div>
            <div
              className={cn(
                styles.hostedFieldContainer,
                formValidation?.invalidIds.includes('#cc-number') &&
                  styles.hostedFieldContainerInError,
                'relative mt-sm',
              )}
              id="cc-number"
              {...getQaTestingProps(QaTestingElements.CardNumber)}
            >
              {formValidation?.invalidIds.includes('#cc-number') &&
                commonErrorMessage}
              <div className="absolute top-3 right-4">
                <PaymentIcon icon={cardTypeBeingWritten} />
              </div>
            </div>
            <div className="flex flex-row mt-sm">
              <div
                className={cn(
                  styles.hostedFieldContainer,
                  formValidation?.invalidIds.includes('#cc-expiration') &&
                    styles.hostedFieldContainerInError,
                )}
                id="cc-expiration"
                {...getQaTestingProps(QaTestingElements.CardExpiry)}
              >
                {formValidation?.invalidIds.includes('#cc-expiration') &&
                  commonErrorMessage}
              </div>
              <div
                className={cn(
                  styles.hostedFieldContainer,
                  formValidation?.invalidIds.includes('#cc-cvv') &&
                    styles.hostedFieldContainerInError,
                  'relative ml-sm',
                )}
                id="cc-cvv"
                {...getQaTestingProps(QaTestingElements.CardCCV)}
              >
                {!loading && !error && (
                  <div className="absolute top-4 right-4">
                    <Tooltip
                      tooltip={
                        <span className="opacity-40">
                          <QuestionMarkIcon />
                        </span>
                      }
                    >
                      <div className="w-tip h-full space-y-xs">
                        <Paragraph>{t('Where do I find the CVV?')}</Paragraph>
                        <img
                          className="w-full h-full"
                          alt={t('Where do I find the CVV?')}
                          src="/images/cc-cvv.png"
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={cn(loading ? 'hidden' : 'flex mt-md space-x-2')}>
            {navigation}
            <PayButton
              onClick={() => submitCardForm()}
              title={
                PaymentStore.flow === PaymentIntentFlow.Manage
                  ? t('Add new card')
                  : t('Proceed to pay')
              }
              disabled={
                !formValidation?.isFilled ||
                formValidation?.invalidIds.length > 0
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
